import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { handleGetLeagues } from "../../shared/controllers/leagueController";
import LoadingGrid from "../../utils/loadingGrid";
import Banner from "../components/Banner";
import Footer from "../components/Footer";
import Navbar from "../components/Navbar";
import landingAsset from "../utils/assetPaths";

interface LeagueItem {
    id: string | number;
    name: string;
    description: string;
    image?: string;
}

const Leagues: React.FC = () => {
    const [leagues, setLeagues] = useState<LeagueItem[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchLeagues = async () => {
            try {
                const data = await handleGetLeagues("user");
                if (data) {
                    const leaguesData = (Array.isArray(data.data) ? data.data : []) as LeagueItem[];
                    setLeagues(leaguesData);
                }
            } catch (error) {
                console.error(error);
            } finally {
                setLoading(false);
            }
        };
        fetchLeagues();
    }, []);

    return (
        <>
            <Navbar />
            <div
                style={{
                    backgroundImage: `url(${landingAsset("herobg.svg")})`,
                    backgroundSize: "cover",
                }}
                className="h-[500px] md:h-[400px] relative w-full flex justify-center items-center flex-col "
            >
                <div
                    style={{
                        background:
                            "linear-gradient(180deg, rgba(0, 0, 0, 0.8) 0%, rgba(0, 0, 0, 0) 17.83%)",
                    }}
                    className=" h-full w-full absolute left-0 top-0 text-center flex flex-col items-center justify-center p-4"
                >
                    <h1 className="text-white text-4xl md:text-5xl lg:text-6xl font-black font-Manrope uppercase tracking-tight">Soccer Leagues</h1>
                    <p className=" mt-6 text-center font-Manrope text-lg md:text-xl text-white/90 max-w-2xl leading-relaxed">
                        Fuel your competitive spirit. Join premium leagues, track your stats, and compete for glory across Nigeria and beyond.
                    </p>
                </div>
            </div>

            <div className="py-24 bg-white px-4 md:px-14">
                <div className="max-w-7xl mx-auto">
                    <div className="flex flex-col md:flex-row md:items-end justify-between gap-10 mb-20">
                        <div className="space-y-4">
                            <h3 className="text-green-700 font-black uppercase tracking-[0.25em] text-sm">Competition</h3>
                            <h2 className="text-4xl md:text-5xl font-black text-gray-900 uppercase tracking-tighter">Elite Leagues</h2>
                        </div>
                        <p className="text-gray-400 font-bold italic text-xl max-w-md">Rise through the ranks. Join a league that matches your skill level and drive.</p>
                    </div>

                    {loading ? (
                        <LoadingGrid count={6} />
                    ) : leagues.length === 0 ? (
                        <div className="bg-gray-50 py-32 rounded-[64px] text-center border-2 border-dashed border-gray-100 italic font-bold text-gray-400 text-2xl">
                            No leagues are currently open for registration.
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-16">
                            {leagues.map((league, i) => (
                                <div key={i} className="group bg-gray-50 rounded-[64px] p-10 border border-gray-100 hover:bg-white hover:shadow-2xl transition-all duration-700 font-Manrope">
                                    <div className="flex flex-col items-center text-center space-y-8">
                                        <div className="w-40 h-40 bg-white rounded-full p-4 shadow-xl group-hover:scale-110 transition-transform duration-500 overflow-hidden">
                                            <img src={league.image || landingAsset("stadia.svg")} alt="" className="w-full h-full object-contain" />
                                        </div>

                                        <div className="space-y-4">
                                            <h3 className="text-3xl font-black text-gray-900 group-hover:text-green-700 transition-colors uppercase tracking-tight leading-none">{league.name}</h3>
                                            <p className="text-gray-400 font-black uppercase tracking-widest text-[10px] bg-gray-100 px-4 py-1.5 rounded-full inline-block">Active Season</p>
                                        </div>

                                        <div className="w-full pt-8 border-t border-gray-100 space-y-4">
                                            <p className="text-gray-500 font-bold italic line-clamp-2">{league.description}</p>
                                            <div className="pt-4">
                                                <Link to={`/leagues/${league.id}`} className="block w-full py-5 bg-gray-900 group-hover:bg-green-700 text-white rounded-[24px] font-black text-lg transition-all shadow-xl active:scale-95">Explore League</Link>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <Banner />
            <Footer />
        </>
    );
};

export default Leagues;
